/**
 * Countries with their states/provinces for cascading dropdowns.
 *
 * Nigeria uses the full LGA dataset from @some19ice/nigeria-geo-core (see nigerianStates.ts).
 * Other countries have states/provinces listed here. LGAs are Nigeria-only.
 */

export interface Country {
  code: string;
  name: string;
  hasStates: boolean;
}

export interface StateProvince {
  name: string;
  countryCode: string;
}

// ── Countries (sorted alphabetically) ──
export const COUNTRIES: Country[] = [
  { code: 'NG', name: 'Nigeria', hasStates: true },
  { code: 'GH', name: 'Ghana', hasStates: true },
  { code: 'KE', name: 'Kenya', hasStates: true },
  { code: 'ZA', name: 'South Africa', hasStates: true },
  { code: 'EG', name: 'Egypt', hasStates: true },
  { code: 'TZ', name: 'Tanzania', hasStates: true },
  { code: 'UG', name: 'Uganda', hasStates: true },
  { code: 'RW', name: 'Rwanda', hasStates: true },
  { code: 'ET', name: 'Ethiopia', hasStates: true },
  { code: 'CM', name: 'Cameroon', hasStates: true },
  { code: 'CI', name: "Cote d'Ivoire", hasStates: true },
  { code: 'SN', name: 'Senegal', hasStates: true },
  { code: 'GB', name: 'United Kingdom', hasStates: true },
  { code: 'US', name: 'United States', hasStates: true },
  { code: 'CA', name: 'Canada', hasStates: true },
  { code: 'AE', name: 'United Arab Emirates', hasStates: true },
  { code: 'IN', name: 'India', hasStates: true },
  { code: 'CN', name: 'China', hasStates: true },
  { code: 'DE', name: 'Germany', hasStates: true },
  { code: 'FR', name: 'France', hasStates: true },
].sort((a, b) => a.name.localeCompare(b.name));

// ── States/Provinces per country (Nigeria handled separately via nigerianStates.ts) ──
const STATES_BY_COUNTRY: Record<string, string[]> = {
  GH: [
    'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern',
    'Greater Accra', 'North East', 'Northern', 'Oti', 'Savannah',
    'Upper East', 'Upper West', 'Volta', 'Western', 'Western North',
  ],
  KE: [
    'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu',
    'Garissa', 'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho',
    'Kiambu', 'Kilifi', 'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui',
    'Kwale', 'Laikipia', 'Lamu', 'Machakos', 'Makueni', 'Mandera',
    'Marsabit', 'Meru', 'Migori', 'Mombasa', 'Murang\'a', 'Nairobi',
    'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri',
    'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi',
    'Trans-Nzoia', 'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot',
  ],
  ZA: [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
  ],
  EG: [
    'Alexandria', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Cairo',
    'Dakahlia', 'Damietta', 'Faiyum', 'Gharbia', 'Giza', 'Ismailia',
    'Kafr El Sheikh', 'Luxor', 'Matruh', 'Minya', 'Monufia', 'New Valley',
    'North Sinai', 'Port Said', 'Qalyubia', 'Qena', 'Red Sea', 'Sharqia',
    'Sohag', 'South Sinai', 'Suez',
  ],
  TZ: [
    'Arusha', 'Dar es Salaam', 'Dodoma', 'Geita', 'Iringa', 'Kagera',
    'Katavi', 'Kigoma', 'Kilimanjaro', 'Lindi', 'Manyara', 'Mara',
    'Mbeya', 'Morogoro', 'Mtwara', 'Mwanza', 'Njombe', 'Pemba North',
    'Pemba South', 'Pwani', 'Rukwa', 'Ruvuma', 'Shinyanga', 'Simiyu',
    'Singida', 'Songwe', 'Tabora', 'Tanga', 'Zanzibar North',
    'Zanzibar South', 'Zanzibar West',
  ],
  UG: [
    'Central', 'Eastern', 'Northern', 'Western',
  ],
  RW: [
    'East', 'Kigali', 'North', 'South', 'West',
  ],
  ET: [
    'Addis Ababa', 'Afar', 'Amhara', 'Benishangul-Gumuz', 'Dire Dawa',
    'Gambela', 'Harari', 'Oromia', 'Sidama', 'Somali', 'South West Ethiopia',
    'Southern Nations', 'Tigray',
  ],
  CM: [
    'Adamawa', 'Centre', 'East', 'Far North', 'Littoral', 'North',
    'North-West', 'South', 'South-West', 'West',
  ],
  CI: [
    'Abidjan', 'Bas-Sassandra', 'Comoe', 'Denguele', 'Goh-Djiboua',
    'Lacs', 'Lagunes', 'Montagnes', 'Sassandra-Marahoue', 'Savanes',
    'Vallee du Bandama', 'Woroba', 'Yamoussoukro', 'Zanzan',
  ],
  SN: [
    'Dakar', 'Diourbel', 'Fatick', 'Kaffrine', 'Kaolack', 'Kedougou',
    'Kolda', 'Louga', 'Matam', 'Saint-Louis', 'Sedhiou', 'Tambacounda',
    'Thies', 'Ziguinchor',
  ],
  GB: [
    'England', 'Northern Ireland', 'Scotland', 'Wales',
  ],
  US: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
  ],
  CA: [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan',
    'Yukon',
  ],
  AE: [
    'Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah',
    'Sharjah', 'Umm Al Quwain',
  ],
  IN: [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh',
    'Chandigarh', 'Puducherry',
  ],
  CN: [
    'Anhui', 'Beijing', 'Chongqing', 'Fujian', 'Gansu', 'Guangdong',
    'Guangxi', 'Guizhou', 'Hainan', 'Hebei', 'Heilongjiang', 'Henan',
    'Hong Kong', 'Hubei', 'Hunan', 'Inner Mongolia', 'Jiangsu', 'Jiangxi',
    'Jilin', 'Liaoning', 'Macau', 'Ningxia', 'Qinghai', 'Shaanxi',
    'Shandong', 'Shanghai', 'Shanxi', 'Sichuan', 'Tianjin', 'Tibet',
    'Xinjiang', 'Yunnan', 'Zhejiang',
  ],
  DE: [
    'Baden-Wurttemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen',
    'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern',
    'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland',
    'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia',
  ],
  FR: [
    'Auvergne-Rhone-Alpes', 'Bourgogne-Franche-Comte', 'Brittany',
    'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Hauts-de-France',
    'Ile-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie',
    'Pays de la Loire', "Provence-Alpes-Cote d'Azur",
  ],
};

/**
 * Get states/provinces for a country.
 * For Nigeria, use NIGERIAN_STATES from nigerianStates.ts for the full dataset with LGA support.
 * This function returns simple string arrays for all countries including Nigeria (for consistency).
 */
export function getStatesForCountry(countryCode: string): string[] {
  return (STATES_BY_COUNTRY[countryCode] || []).sort();
}

/**
 * Find country name from code.
 */
export function getCountryName(code: string): string {
  const c = COUNTRIES.find((ct) => ct.code === code);
  return c?.name || code;
}

/**
 * Check if a country has LGA support (only Nigeria for now).
 */
export function hasLGASupport(countryCode: string): boolean {
  return countryCode === 'NG';
}
